import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Lock, Database, UserCheck, Clock } from 'lucide-react';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen py-20">
      <div className="container max-w-4xl mx-auto">
        {/* Back Navigation */}
        <Link 
          to="/" 
          className="inline-flex items-center text-[var(--text-secondary)] hover:text-[var(--accent-primary)] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="text-center mb-16">
          <div className="w-16 h-16 bg-[var(--accent-bg)] rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-[var(--accent-primary)]" />
          </div>
          <h1 className="display-md text-[var(--text-primary)] mb-6">Privacy Policy</h1>
          <p className="body-lg max-w-2xl mx-auto">
            Your privacy is important to us. This policy explains how Marqi collects, uses, and protects your personal information.
          </p>
          <div className="text-sm text-[var(--text-muted)] mt-4">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>

        {/* Quick Overview */}
        <section className="mb-16 bg-[var(--bg-secondary)] p-8 rounded-2xl">
          <h2 className="h2 text-[var(--text-primary)] mb-6 flex items-center">
            <Eye className="w-6 h-6 mr-3 text-[var(--accent-primary)]" />
            Privacy at a Glance
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-[var(--accent-bg)] rounded-lg flex items-center justify-center mx-auto mb-3">
                <Lock className="w-6 h-6 text-[var(--accent-primary)]" />
              </div>
              <h3 className="font-semibold text-[var(--text-primary)] mb-2">Secure Collection</h3>
              <p className="text-sm text-[var(--text-secondary)]">We only collect information necessary to provide our services</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-[var(--accent-bg)] rounded-lg flex items-center justify-center mx-auto mb-3">
                <Database className="w-6 h-6 text-[var(--accent-primary)]" />
              </div>
              <h3 className="font-semibold text-[var(--text-primary)] mb-2">Protected Storage</h3>
              <p className="text-sm text-[var(--text-secondary)]">Your data is encrypted and stored securely</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-[var(--accent-bg)] rounded-lg flex items-center justify-center mx-auto mb-3">
                <UserCheck className="w-6 h-6 text-[var(--accent-primary)]" />
              </div>
              <h3 className="font-semibold text-[var(--text-primary)] mb-2">Your Control</h3>
              <p className="text-sm text-[var(--text-secondary)]">You control what information you share with us</p>
            </div>
          </div>
        </section>

        {/* Detailed Sections */}
        <div className="space-y-12">
          {/* Information We Collect */}
          <section>
            <h2 className="h2 text-[var(--text-primary)] mb-6">1. Information We Collect</h2>
            <div className="space-y-6">
              <div className="p-6 bg-[var(--bg-secondary)] rounded-lg">
                <h3 className="h3 text-[var(--text-primary)] mb-3">Contact Information</h3>
                <ul className="space-y-2 text-[var(--text-secondary)]">
                  <li>• Name and business title</li>
                  <li>• Email address and phone number</li>
                  <li>• Company name and website</li>
                  <li>• Service requirements and budget information</li>
                </ul>
              </div>
              <div className="p-6 bg-[var(--bg-secondary)] rounded-lg">
                <h3 className="h3 text-[var(--text-primary)] mb-3">Technical Information</h3>
                <ul className="space-y-2 text-[var(--text-secondary)]">
                  <li>• Website analytics and usage patterns</li>
                  <li>• Device information and browser type</li>
                  <li>• IP address and geographic location</li>
                  <li>• Cookies and tracking technologies</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Information */}
          <section>
            <h2 className="h2 text-[var(--text-primary)] mb-6">2. How We Use Your Information</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 bg-[var(--bg-secondary)] rounded-lg">
                <h3 className="h3 text-[var(--text-primary)] mb-3">Service Delivery</h3>
                <ul className="space-y-2 text-[var(--text-secondary)]">
                  <li>• Respond to inquiries and provide consultations</li>
                  <li>• Deliver advertising and marketing services</li>
                  <li>• Process payments and manage accounts</li>
                  <li>• Provide customer support and communication</li>
                </ul>
              </div>
              <div className="p-6 bg-[var(--bg-secondary)] rounded-lg">
                <h3 className="h3 text-[var(--text-primary)] mb-3">Business Operations</h3>
                <ul className="space-y-2 text-[var(--text-secondary)]">
                  <li>• Improve our website and services</li>
                  <li>• Send relevant industry updates and insights</li>
                  <li>• Analyze usage patterns and performance</li>
                  <li>• Comply with legal and regulatory requirements</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Information Sharing */}
          <section>
            <h2 className="h2 text-[var(--text-primary)] mb-6">3. Information Sharing</h2>
            <div className="p-6 bg-[var(--bg-secondary)] rounded-lg border-l-4 border-[var(--accent-primary)]">
              <p className="text-[var(--text-secondary)] mb-4">
                <strong className="text-[var(--text-primary)]">We do not sell your personal information.</strong> 
                We may share your information only in the following circumstances:
              </p>
              <ul className="space-y-2 text-[var(--text-secondary)]">
                <li>• With trusted service providers who assist in our operations</li>
                <li>• When required by law or to protect our legal rights</li>
                <li>• With your explicit consent for specific purposes</li>
                <li>• In connection with a business merger or acquisition (with notice)</li>
              </ul>
            </div>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="h2 text-[var(--text-primary)] mb-6">4. Data Security</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 bg-[var(--bg-secondary)] rounded-lg">
                <h3 className="h3 text-[var(--text-primary)] mb-3">Technical Safeguards</h3>
                <ul className="space-y-2 text-[var(--text-secondary)]">
                  <li>• SSL/TLS encryption for data transmission</li>
                  <li>• Encrypted database storage</li>
                  <li>• Regular security audits and updates</li>
                  <li>• Access controls and authentication</li>
                </ul>
              </div>
              <div className="p-6 bg-[var(--bg-secondary)] rounded-lg">
                <h3 className="h3 text-[var(--text-primary)] mb-3">Operational Safeguards</h3>
                <ul className="space-y-2 text-[var(--text-secondary)]">
                  <li>• Limited access on a need-to-know basis</li>
                  <li>• Employee privacy training and agreements</li>
                  <li>• Incident response and breach notification procedures</li>
                  <li>• Regular backup and data recovery systems</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="h2 text-[var(--text-primary)] mb-6">5. Your Rights and Choices</h2>
            <div className="space-y-4">
              <div className="p-6 bg-[var(--bg-secondary)] rounded-lg flex items-start space-x-4">
                <div className="w-8 h-8 bg-[var(--accent-bg)] rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <Eye className="w-4 h-4 text-[var(--accent-primary)]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[var(--text-primary)] mb-2">Access and Portability</h3>
                  <p className="text-[var(--text-secondary)]">Request a copy of your personal information and data in a portable format</p>
                </div>
              </div>
              <div className="p-6 bg-[var(--bg-secondary)] rounded-lg flex items-start space-x-4">
                <div className="w-8 h-8 bg-[var(--accent-bg)] rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <UserCheck className="w-4 h-4 text-[var(--accent-primary)]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[var(--text-primary)] mb-2">Correction and Updates</h3>
                  <p className="text-[var(--text-secondary)]">Update or correct any inaccurate personal information we have</p>
                </div>
              </div>
              <div className="p-6 bg-[var(--bg-secondary)] rounded-lg flex items-start space-x-4">
                <div className="w-8 h-8 bg-[var(--accent-bg)] rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <Lock className="w-4 h-4 text-[var(--accent-primary)]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[var(--text-primary)] mb-2">Deletion and Restriction</h3>
                  <p className="text-[var(--text-secondary)]">Request deletion of your data or restriction of certain processing activities</p>
                </div>
              </div>
            </div>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="h2 text-[var(--text-primary)] mb-6">6. Cookies and Tracking</h2>
            <div className="p-6 bg-[var(--bg-secondary)] rounded-lg">
              <p className="text-[var(--text-secondary)] mb-4">
                We use cookies and similar technologies to enhance your browsing experience:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-[var(--text-primary)] mb-2">Essential Cookies</h4>
                  <p className="text-sm text-[var(--text-secondary)]">Required for basic website functionality and security</p>
                </div>
                <div>
                  <h4 className="font-semibold text-[var(--text-primary)] mb-2">Analytics Cookies</h4>
                  <p className="text-sm text-[var(--text-secondary)]">Help us understand website usage and improve performance</p>
                </div>
              </div>
              <div className="mt-4 p-4 bg-[var(--accent-bg)] rounded-lg">
                <p className="text-sm text-[var(--text-primary)]">
                  You can manage cookie preferences in your browser settings. Some features may not work properly if you disable certain cookies.
                </p>
              </div>
            </div>
          </section>

          {/* Retention */}
          <section>
            <h2 className="h2 text-[var(--text-primary)] mb-6 flex items-center">
              <Clock className="w-6 h-6 mr-3 text-[var(--accent-primary)]" />
              7. Data Retention
            </h2>
            <div className="p-6 bg-[var(--bg-secondary)] rounded-lg">
              <p className="text-[var(--text-secondary)] mb-4">
                We retain your personal information only as long as necessary for:
              </p>
              <ul className="space-y-2 text-[var(--text-secondary)]">
                <li>• Providing our services and maintaining our business relationship</li>
                <li>• Complying with legal obligations and resolving disputes</li>
                <li>• Improving our services and maintaining security</li>
              </ul>
              <div className="mt-4 p-4 bg-[var(--bg-tertiary)] rounded-lg">
                <p className="text-sm text-[var(--text-secondary)]">
                  <strong>Typical retention periods:</strong> Contact information (3 years after last contact), 
                  Analytics data (2 years), Customer project data (5 years after project completion)
                </p>
              </div>
            </div>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="h2 text-[var(--text-primary)] mb-6">8. Contact Us</h2>
            <div className="p-8 bg-[var(--bg-secondary)] rounded-lg text-center">
              <p className="text-[var(--text-secondary)] mb-6">
                If you have any questions about this Privacy Policy or how we handle your personal information, 
                please don't hesitate to contact us:
              </p>
              <div className="space-y-4">
                <div>
                  <strong className="text-[var(--text-primary)]">Email:</strong>{' '}
                  <a href="mailto:info@marqi.io" className="text-[var(--accent-primary)] hover:underline">
                    info@marqi.io
                  </a>
                </div>
                <div>
                  <strong className="text-[var(--text-primary)]">Address:</strong>{' '}
                  <span className="text-[var(--text-secondary)]">
                    Marqi Digital Agency, 123 Innovation Drive, Suite 200, San Francisco, CA 94107
                  </span>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-[var(--border-subtle)]">
                <Link 
                  to="/contact" 
                  className="btn-primary inline-flex items-center"
                >
                  Contact Our Privacy Team
                </Link>
              </div>
            </div>
          </section>
        </div>

        {/* Footer Note */}
        <div className="mt-16 p-6 bg-[var(--accent-bg)] rounded-lg text-center">
          <p className="text-sm text-[var(--text-primary)]">
            <strong>Changes to This Policy:</strong> We may update this Privacy Policy occasionally. 
            We'll notify you of significant changes via email or website notice. 
            Your continued use of our services constitutes acceptance of the updated policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;